import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../lib/getApiUrl';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  FileText, 
  Shield, 
  AlertTriangle,
  RefreshCw,
  Eye,
  Download,
  Upload
} from 'lucide-react';

interface ContractorVerification {
  id: string;
  contractorId: string;
  contractorName: string;
  company: string;
  email: string;
  licenseNumber?: string;
  licenseState?: string;
  licenseExpiryDate?: string;
  licenseVerified: boolean;
  licenseVerifiedAt?: string;
  licenseVerifiedBy?: string;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiryDate?: string;
  insuranceVerified: boolean;
  insuranceVerifiedAt?: string;
  insuranceVerifiedBy?: string;
  insuranceAmount?: number;
  stripeAccountId?: string;
  stripePayoutReady?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface VerificationDocument {
  id: string;
  contractorId: string;
  documentType: 'LICENSE' | 'INSURANCE' | 'CERTIFICATION' | 'OTHER';
  fileName: string;
  s3Key: string;
  uploadedAt: string;
  verifiedAt?: string;
  verifiedBy?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
}

const ContractorVerificationWorkflow: React.FC = () => {
  const [contractors, setContractors] = useState<ContractorVerification[]>([]);
  const [documents, setDocuments] = useState<VerificationDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContractor, setSelectedContractor] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchContractorData();
  }, []);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl('/api/admin/contractors/verification'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contractor verification data');
      }

      const data = await response.json();
      setContractors(data.contractors || []);
      setDocuments(data.documents || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchContractorData();
    setRefreshing(false);
  };

  const verifyLicense = async (contractorId: string, verified: boolean) => {
    try {
      const response = await fetch(`/api/admin/contractors/${contractorId}/license/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
        body: JSON.stringify({ verified }),
      });

      if (!response.ok) {
        throw new Error('Failed to update license verification');
      }

      await fetchContractorData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify license');
    }
  };

  const verifyInsurance = async (contractorId: string, verified: boolean) => {
    try {
      const response = await fetch(`/api/admin/contractors/${contractorId}/insurance/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('clerk_token')}`,
        },
        body: JSON.stringify({ verified }),
      });

      if (!response.ok) {
        throw new Error('Failed to update insurance verification');
      }

      await fetchContractorData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify insurance');
    }
  };

  const getVerificationStatus = (contractor: ContractorVerification) => {
    const licenseStatus = contractor.licenseVerified ? 'verified' : 'pending';
    const insuranceStatus = contractor.insuranceVerified ? 'verified' : 'pending';
    const stripeStatus = contractor.stripePayoutReady ? 'verified' : 'pending';

    if (licenseStatus === 'verified' && insuranceStatus === 'verified' && stripeStatus === 'verified') {
      return { status: 'fully-verified', color: 'bg-green-100 text-green-800 border-green-200' };
    } else if (licenseStatus === 'pending' || insuranceStatus === 'pending' || stripeStatus === 'pending') {
      return { status: 'pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    } else {
      return { status: 'not-verified', color: 'bg-red-100 text-red-800 border-red-200' };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const pendingVerifications = contractors.filter(c => 
    !c.licenseVerified || !c.insuranceVerified || !c.stripePayoutReady
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading contractor verification data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contractor Verification</h1>
          <p className="text-gray-600 mt-1">Manage contractor license, insurance, and Stripe Connect verification</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={refreshData}
            disabled={refreshing}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Pending Verifications Alert */}
      {pendingVerifications.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>{pendingVerifications.length} contractor(s)</strong> require verification.
            <Button variant="link" className="p-0 ml-2 text-yellow-600">
              View Details
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error State */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Contractors List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {contractors.map((contractor) => {
          const verificationStatus = getVerificationStatus(contractor);
          
          return (
            <Card key={contractor.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-blue-500" />
                    <span className="text-gray-700">{contractor.company}</span>
                  </div>
                  <Badge className={verificationStatus.color}>
                    {verificationStatus.status.replace('-', ' ')}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contractor Info */}
                <div>
                  <p className="font-medium text-gray-900">{contractor.contractorName}</p>
                  <p className="text-sm text-gray-600">{contractor.email}</p>
                </div>

                {/* License Verification */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(contractor.licenseVerified ? 'verified' : 'pending')}
                      <span className="text-sm font-medium">License</span>
                    </div>
                    {contractor.licenseNumber && (
                      <span className="text-xs text-gray-500">{contractor.licenseNumber}</span>
                    )}
                  </div>
                  {contractor.licenseVerified ? (
                    <p className="text-xs text-green-600">
                      Verified {contractor.licenseVerifiedAt && new Date(contractor.licenseVerifiedAt).toLocaleDateString()}
                    </p>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => verifyLicense(contractor.id, true)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => verifyLicense(contractor.id, false)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>

                {/* Insurance Verification */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(contractor.insuranceVerified ? 'verified' : 'pending')}
                      <span className="text-sm font-medium">Insurance</span>
                    </div>
                    {contractor.insuranceProvider && (
                      <span className="text-xs text-gray-500">{contractor.insuranceProvider}</span>
                    )}
                  </div>
                  {contractor.insuranceVerified ? (
                    <p className="text-xs text-green-600">
                      Verified {contractor.insuranceVerifiedAt && new Date(contractor.insuranceVerifiedAt).toLocaleDateString()}
                    </p>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => verifyInsurance(contractor.id, true)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => verifyInsurance(contractor.id, false)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>

                {/* Stripe Connect Status */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(contractor.stripePayoutReady ? 'verified' : 'pending')}
                      <span className="text-sm font-medium">Stripe Connect</span>
                    </div>
                    <div className="flex space-x-1">
                      {contractor.stripePayoutReady && (
                        <Badge variant="secondary" className="text-xs">Payouts enabled</Badge>
                      )}
                    </div>
                  </div>
                  {contractor.stripePayoutReady ? (
                    <p className="text-xs text-green-600">Onboarding Complete</p>
                  ) : (
                    <p className="text-xs text-yellow-600">Onboarding Required</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex space-x-2 pt-2 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedContractor(contractor.id)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Details
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Documents
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {contractors.length === 0 && !loading && (
        <Card>
          <CardContent className="text-center py-12">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Contractors Found</h3>
            <p className="text-gray-600 mb-4">
              No contractor verification data available. Contractors will appear here once they register and submit verification documents.
            </p>
            <Button variant="outline">
              Refresh Data
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContractorVerificationWorkflow;

